import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    Box,
    Typography,
    LinearProgress,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    FormControlLabel,
    Radio,
    RadioGroup,
    Slider,
    Card,
    CardHeader,
    CardMedia,
    CardContent,
    CardActions,
    Collapse,
    Avatar,
    IconButton,
    Paper,
    Divider,
    Modal,
    Step,
    Stepper,
    StepLabel,
    Tab,
    Tabs,
    Stack
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { formatIndianCurrency, formatIndianCurrencyCompact, formatPercentage } from '../utils/currency';
// import { DataGrid } from "@mui/x-data-grid";
// import { styled } from "@mui/material/styles";
// import { red } from "@mui/material/colors";
// import FavoriteIcon from "@mui/icons-material/Favorite";
// import ShareIcon from "@mui/icons-material/Share";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment, { min, now } from "moment";
import Fab from "@mui/material/Fab";
import CloseIcon from "@mui/icons-material/Close";
// import EditIcon from "@mui/icons-material/CreateOutlined";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import BotIcon from "@mui/icons-material/SmartToyOutlined";
import { ToastContainer, toast, Bounce } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
// import Table from '@mui/material/Table';
// import TableBody from '@mui/material/TableBody';
// import TableCell, { tableCellClasses } from '@mui/material/TableCell';
// import TableContainer from '@mui/material/TableContainer';
// import TableHead from '@mui/material/TableHead';
// import TableRow from '@mui/material/TableRow';
import { fetchOpenPositionsData, exitTrade, modifyTrade } from "../services/api";
import { 
    getAutoRefreshIntervalFromLocalStorage, 
    setAutoRefreshIntervalInLocalStorage
} from "../utils/data";


const OpenPositions = ({ userAccountId }) => {
    const [autoRefreshInterval, setAutoRefreshInterval] = useState(getAutoRefreshIntervalFromLocalStorage());
    const [autoRefreshCountdownProgress, setAutoRefreshCountdownProgress] = useState(90);
    const [openPositions, setOpenPositions] = useState([]);
    const [openPositionsUpdatedAt, setOpenPositionsUpdatedAt] = useState(null);

    useEffect(() => {
        fetchOpenPositions();

        const intervalId = setInterval(() => {
            fetchOpenPositions();
        }, autoRefreshInterval * 1000);

        const step = 100 / (autoRefreshInterval * 10);
        // Set up a separate interval to update the countdown every second
        const countdownIntervalId = setInterval(() => {
            setAutoRefreshCountdownProgress((prev) => (prev + step) % 101);
        }, 100);

        return () => {
            clearInterval(intervalId);
            clearInterval(countdownIntervalId);
        }
    }, [autoRefreshInterval, userAccountId]);

    const fetchOpenPositions = () => {
        fetchOpenPositionsData({ user_account_id: userAccountId })
            .then((response) => {
                setOpenPositions(response.data.positions);
                setOpenPositionsUpdatedAt(Date.now());
            })
            .catch((error) => {
                // console.error("Error fetching trades:", error);
                showToast("error", "Error fetching Open Positions!\n" + error.message);
            });
    };

    const showToast = (type, message) => {
        let properties = {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
        }
        switch (type) {
            case "success":
                toast.success(message, properties);
                break;
            case "error":
                toast.error(message, properties);
                break;
            case "warning":
                toast.warn(message, properties);
                break;
            default:
                toast.info(message, properties);
        }
    }
    return (

        <Card sx={{
            // display: { xs: 'none', md: 'block' },
            backgroundColor: 'background.paper',
            p: 2
        }}>
            <Box display="flex" justifyContent="space-between" alignItems={"center"} sx={{ mx: 2 }}>
                <Stack direction="row" sx={{ gap: 2 }}>
                    <Typography variant="h6">Open Positions</Typography>
                    <CircularProgress variant="determinate" value={autoRefreshCountdownProgress} size={20} />
                    {openPositionsUpdatedAt &&
                        <Typography variant="body2" sx={{ p: 1, color: moment(openPositionsUpdatedAt).isBefore(moment(Date.now()).subtract(10000)) ? `#D32F2F` : `#636363` }}>
                            (updated {moment(Date.now()).subtract(moment(openPositionsUpdatedAt)).seconds()} second(s) ago...)
                        </Typography>
                    }
                </Stack>
                <FormControl margin="normal">
                    <InputLabel>Refresh Interval</InputLabel>
                    <Select
                        value={autoRefreshInterval}
                        onChange={(e) => {
                            setAutoRefreshInterval(Number(e.target.value));
                            setAutoRefreshIntervalInLocalStorage(Number(e.target.value));
                        }}
                    >
                        <MenuItem value={1}>1 second</MenuItem>
                        <MenuItem value={2}>2 seconds</MenuItem>
                        <MenuItem value={5}>5 seconds</MenuItem>
                        <MenuItem value={60}>1 minute</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {openPositions.map((position) => <OpenPosition fetchOpenPositions={fetchOpenPositions} showToast={showToast} position={position} openPositions={openPositions}></OpenPosition>)}
        </Card>
    )
}

/**
 * OpenPosition component displays individual position details with modification slider
 * 
 * Expected position object structure:
 * - stop_loss_price: number - Absolute stop loss price
 * - target_price: number - Absolute target price  
 * - buying_price: number - Entry price
 * - instrument.ltp: number - Current market price (Last Traded Price)
 * - stop_loss_trailing_factor: number - Trailing stop loss factor
 * - Other display fields: id, quantity, margin_utilised, unrealized_pnl, etc.
 */
const OpenPosition = ({ fetchOpenPositions, position, showToast, openPositions }) => {
    const [stopLossTrailingFactor, setStopLossTrailingFactor] = useState((position.stop_loss_trailing_factor)?.toString());
    const [loading, setLoading] = useState(false);

    // Handle exiting a trade
    const handleExitTrade = (tradeId) => {
        setLoading(true);
        // Balance updates are handled by backend; local calculations removed
        exitTrade(tradeId)
            .then((response) => {
                showToast("success", `Position squared off successfully!`);
                fetchOpenPositions();
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error exiting trade:", error);
                showToast("error", "Error exiting trade!");
                setLoading(false);
            });
    };

    const handleModifyTrade = (id, stop_loss_price, target_price, stop_loss_trailing_strategy_version, stop_loss_trailing_factor, stop_loss_reference_price) => {
        setLoading(true);
        let orderDetails = {
            stop_loss_price: parseFloat(stop_loss_price),
            target_price: parseFloat(target_price),
            stop_loss_trailing_strategy_version: stop_loss_trailing_strategy_version,
            stop_loss_trailing_factor: parseFloat(stop_loss_trailing_factor),
            stop_loss_reference_price: parseFloat(stop_loss_reference_price),
        }
        modifyTrade(id, orderDetails)
            .then(() => {
                fetchOpenPositions();
                showToast("success", "Trade modified successfully!");
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error modifying trade:", error);
                showToast("error", "Error modifying trade!");
            });
    };


    function formatPrice(value) {
        const isNegative = value < 0;
        const formattedValue = `₹${Math.abs(value)?.toFixed(2).toLocaleString()}`;
        return isNegative ? `-${formattedValue}` : formattedValue;
    }

    function formatChange(value) {
        return formatPercentage(value, true);
    }

    function formatCurrency(value) {
        return formatIndianCurrency(value);
    }

    function formatPercentageUnsigned(value) {
        const formattedValue = `${Math.abs(value)?.toFixed(2).toLocaleString()}%`;
        return formattedValue;
    }

    function getFontColor(value) {
        const isNegative = value < 0;
        return isNegative ? `#D32F2F` : `#4CAA50`;
    }

    function compare(a, b) {
        if (a.value < b.value) {
            return -1;
        }
        if (a.value > b.value) {
            return 1;
        }
        return 0;
    }

    function getOrderModificationSliderProperties(position) {
        let marks = [
            {
                value: position.stop_loss_price?.toFixed(2),
                label: `SL`
                // label: `Stop Loss (${formatPrice(position.stop_loss_price)})`,
            },
            {
                value: position.buying_price?.toFixed(2),
                label: `Entry`,
                // label: `Entry (${formatPrice(position.buying_price)})`,
            },
            {
                value: position.instrument.ltp?.toFixed(2),
                label: `Current`,
                // label: `Current (${formatPrice(position.instrument.ltp)}) (${formatPercentage(position.price_change_percentage)})`,
            },
            {
                value: position.target_price?.toFixed(2),
                label: `Target`
                // label: `Target (${formatPrice(position.target_price)})`,
            },
        ]
        marks.sort(compare);

        // Create a reasonable range around the current price levels
        let priceRange = Math.abs(position.target_price - position.stop_loss_price);
        let minValue = position.stop_loss_price - (priceRange * 0.2);
        let maxValue = position.target_price + (priceRange * 0.2);
        let defaultValue = [position.stop_loss_price?.toFixed(2), position.target_price?.toFixed(2)]

        return { marks, minValue, maxValue, defaultValue }
    }

    let sliderProperties = getOrderModificationSliderProperties(position);

    // return OpenPosition()

    return <Card
        key={position.id}
        sx={{
            p: 2, m: 2, backgroundColor: '#fcfcfc',
        }}
    >
        <CardHeader
            avatar={position.is_automated ? (
                <Avatar sx={{ backgroundColor: "#4CAA50" }}>
                    <BotIcon></BotIcon>
                </Avatar>
            ) : (
                <Avatar sx={{ backgroundColor: "#FFB300" }}>
                    <WarningIcon></WarningIcon>
                </Avatar>
            )}
            action={<Box>
                <Fab
                    sx={{
                        mr: 1,
                        ml: 1,
                        backgroundColor: "#D32F2F",
                        color: "#ffffff",
                        "&:hover": {
                            backgroundColor: "#b32727",
                        },
                    }}
                    size="medium"
                    variant="extended"
                    onClick={() => handleExitTrade(position.id)}
                    disabled={loading}
                >
                    <CloseIcon sx={{ mr: 1 }} />
                    Exit
                </Fab>
            </Box>}
            // titleTypographyProps={{ variant: "body1" }}
            // title={`${position.instrument.index_name} ${position.instrument.strike_price.toLocaleString()} ${position.instrument.option_type}`}
            title={<Grid container spacing={2}>
                <Grid size={4}>
                    <Typography sx={{ fontWeight: "900" }}>
                        {position.instrument.index_name} {position.instrument.strike_price.toLocaleString()} {position.instrument.option_type}
                    </Typography>
                    <Typography variant="subtitle2">{moment(position.instrument.expiry_date).format("Do MMM YYYY")}</Typography>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                        Trade #{position.id}
                    </Typography>
                </Grid>
                <Grid size={4}>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "900" }}>
                        PnL:
                        <Typography variant="p" sx={{ p: 1, color: getFontColor(position.unrealized_pnl) }}>
                            {formatCurrency(position.unrealized_pnl)}
                            <Typography variant="p" sx={{ p: 1 }}>
                                ({formatPercentage(position.unrealized_pnl_percentage)})
                            </Typography>
                        </Typography>
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "900" }}>
                        Invested:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatCurrency(position.margin_utilised)}
                        </Typography>
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "900" }}>
                        Current:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatCurrency(position.margin_utilised + position.unrealized_pnl)}
                        </Typography>
                    </Typography>
                </Grid>
                <Grid size={4}>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "600" }}>
                        Quantity:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {position.quantity}
                        </Typography>
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "600" }}>
                        Max Loss:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatCurrency(position.max_loss)}
                        </Typography>
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "600" }}>
                        Max Profit:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatCurrency(position.max_profit)}
                        </Typography>
                    </Typography>
                </Grid>
            </Grid>} />
        <CardContent sx={{ mt: 2 }}>
            <Grid container spacing={2}>
                {/* <Grid size={12}>
                    <Divider></Divider>
                </Grid>
                <Grid size={4}>
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                        Quantity:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {position.quantity}
                        </Typography>
                    </Typography>
                </Grid>
                <Grid size={4}>
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                        Max Loss:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatCurrency(position.max_loss)}
                        </Typography>
                    </Typography>

                </Grid>
                <Grid size={4}>
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                        Max Profit:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatCurrency(position.max_profit)}
                        </Typography>
                    </Typography>
                </Grid> */}
                <Grid size={12}>
                    <Divider></Divider>
                </Grid>
                <Grid size={3}>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "600" }}>
                        Stop Loss:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatPrice(position.stop_loss_price)} ({formatChange(position.stop_loss_price - position.instrument.ltp)})
                        </Typography>
                    </Typography>
                </Grid>
                <Grid size={3}>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "600" }}>
                        Buying Price:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatPrice(position.buying_price)}
                        </Typography>
                    </Typography>
                </Grid>
                <Grid size={3}>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "600" }}>
                        Current Price:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatPrice(position.instrument.ltp)} ({formatChange(position.instrument.ltp - position.buying_price)})
                            {/* <Typography variant="p" sx={{ p: 1, color: getFontColor(position.unrealized_pnl) }}>
({formatPercentage(position.price_change_percentage)})
</Typography> */}
                        </Typography>
                    </Typography>
                </Grid>
                <Grid size={3}>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "600" }}>
                        Target:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatPrice(position.target_price)} ({formatChange(position.target_price - position.instrument.ltp)})
                        </Typography>
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <Divider></Divider>
                </Grid>
                <Grid size={12}>
                    <Slider
                        sx={{ mt: 4 }}
                        // size="small"
                        value={sliderProperties.defaultValue}
                        onChangeCommitted={(e, value) => {
                            handleModifyTrade(position.id, parseFloat(value[0]), parseFloat(value[1]), 3, parseInt(stopLossTrailingFactor), position.instrument.ltp);
                        }}
                        min={sliderProperties.minValue}
                        max={sliderProperties.maxValue}
                        step={0.05}
                        marks={sliderProperties.marks}
                        valueLabelDisplay="on"
                        disabled={loading}
                    />
                </Grid>
                {/* TODO: Add these later when we have TSL strategy version and factor */}
                {/* <Grid size={2}>
                    <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "600" }}>
                        TSL Ref:
                        <Typography variant="p" sx={{ p: 1, color: "text.primary" }}>
                            {formatPrice(position.stop_loss_reference_price || position.buying_price)}
                        </Typography>
                    </Typography>
                    <br />
                    <TextField label="TSL Factor" type="number" variant="outlined" value={stopLossTrailingFactor} onChange={(e) => setStopLossTrailingFactor(e.target.value)} fullWidth size="small" />
                </Grid> */}
            </Grid>
        </CardContent>
    </Card>
};

export default OpenPositions;
